import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useSnackbar } from 'notistack';

/**
 * 번호 하나로 이 화면을 여는 주소와 복사 버튼.
 *
 * 사람이 주고받는 건 UUID 가 아니라 번호다. 메일이나 메신저에 붙여 놓고 "이거 좀 봐 주세요"
 * 하는 식이라, 그 주소를 눈에 보이게 두고 한 번에 복사할 수 있어야 쓴다.
 */
const ShareLinkBox = ({ path, label = '공유 주소', compact = false, text }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [copied, setCopied] = useState(false);

  if (!path) return null;

  const url = `${window.location.origin}${path}`;

  const copy = async () => {
    // navigator.clipboard 는 https 나 localhost 에서만 있다. 사내 서버는 http 로 열려 있어서
    // 거기서는 아예 undefined 다 - 그냥 쓰면 버튼을 눌러도 아무 일이 안 일어나고
    // 누른 사람은 왜 안 되는지 모른다. 그래서 예전 방식을 같이 둔다.
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('copy failed');
      }
      setCopied(true);
      enqueueSnackbar('주소를 복사했습니다.', { variant: 'success' });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      enqueueSnackbar('복사하지 못했습니다. 주소를 직접 선택해 주세요.', { variant: 'warning' });
    }
  };

  // 목록 칸 안에서는 주소를 다 펼칠 자리가 없다. 번호만 보여 주고 아이콘 하나만 둔다.
  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
        <Tooltip title={copied ? '복사했습니다' : '공유 주소 복사'}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); copy(); }}
            sx={{ p: 0.25 }}
          >
            {copied
              ? <CheckIcon sx={{ fontSize: 14 }} color="success" />
              : <ContentCopyIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontFamily: 'monospace',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          direction: 'rtl',        // 길면 앞이 잘리게 — 뒤쪽 번호가 남아야 알아본다
          textAlign: 'left',
        }}
        title={url}
      >
        {url}
      </Typography>
      <Tooltip title={copied ? '복사했습니다' : '주소 복사'}>
        <IconButton size="small" onClick={copy}>
          {copied ? <CheckIcon fontSize="inherit" color="success" /> : <ContentCopyIcon fontSize="inherit" />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ShareLinkBox;
